/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import SuperAdminContext from './CatalogCsvUploadContext';
import initialCatalogCsvUploadState from './initialCatalogCsvUploadState';
import catalogCsvUploadReducer from './reducer/catalogCsvUpload.reducer';
import * as action from './reducer/catalogCsvUpload.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { IgetCatalogReportsBySeasonAPIResponseItem } from 'api/endpoints/catalogUploads/catalogUploads.response.interface';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';

function CatalogCsvUploadProvider(props: IChildrenProps) {
  const [catalogCsvUploadtate, dispatch] = useReducer(
    catalogCsvUploadReducer,
    initialCatalogCsvUploadState
  );
  const { getAccessTokenSilently, isLoading } = useAuth0();
  const { company } = useContextCompany();
  const { t: translate } = useTranslation();

  const getReportsByUpload = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (isLoading || !token || !company?.brand?.id) return false;
    return await action.getReportsByUploadAction(
      dispatch,
      company.brand.id,
      token,
      translate
    );
  }, [company?.brand?.id, isLoading]);

  const publishUpload = useCallback(
    async (uploadId: string, publish: boolean) => {
      const token = await getAccessTokenSilently();
      if (isLoading || !token || !company?.brand?.id) return false;
      return await action.publishUploadAction(
        dispatch,
        uploadId,
        company.brand?.id,
        publish,
        token,
        translate
      );
    },
    [company?.brand?.id, isLoading]
  );

  const rejectUpload = useCallback(
    async (uploadId: string) => {
      const token = await getAccessTokenSilently();
      if (isLoading || !token || !company?.brand?.id) return false;
      return await action.rejectUploadAction(
        dispatch,
        uploadId,
        company.brand?.id,
        token,
        translate
      );
    },
    [company?.brand?.id, isLoading]
  );

  const getReportsBySeason = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (isLoading || !token || !company?.brand?.id) return false;
    return await action.getReportsBySeasonAction(
      dispatch,
      company.brand.id,
      token,
      translate
    );
  }, [company?.brand?.id, isLoading]);

  const setStartSeasonDate = useCallback(
    async (report: IgetCatalogReportsBySeasonAPIResponseItem, date: any) => {
      const token = await getAccessTokenSilently();
      if (isLoading || !token) return false;
      return await action.setStartSeasonDateAction(
        dispatch,
        report,
        date,
        token,
        translate
      );
    },
    [isLoading]
  );

  const setEndSeasonDate = useCallback(
    async (report: IgetCatalogReportsBySeasonAPIResponseItem, date: any) => {
      const token = await getAccessTokenSilently();
      if (isLoading || !token) return false;
      return await action.setEndSeasonDateAction(
        dispatch,
        report,
        date,
        token,
        translate
      );
    },
    [isLoading]
  );

  const enableSeason = useCallback(
    async (report: IgetCatalogReportsBySeasonAPIResponseItem, enabled: boolean) => {
      const token = await getAccessTokenSilently();
      if (isLoading || !token) return false;
      return await action.enableSeasonAction(dispatch, report, enabled, token, translate);
    },
    [isLoading]
  );

  const memoProvider = useMemo(
    () => ({
      ...catalogCsvUploadtate,
      getReportsByUpload,
      publishUpload,
      rejectUpload,
      getReportsBySeason,
      setStartSeasonDate,
      setEndSeasonDate,
      enableSeason,
    }),
    [
      catalogCsvUploadtate,
      getReportsByUpload,
      publishUpload,
      rejectUpload,
      getReportsBySeason,
      setStartSeasonDate,
      setEndSeasonDate,
      enableSeason,
    ]
  );

  return (
    <SuperAdminContext.Provider value={memoProvider}>
      {props.children}
    </SuperAdminContext.Provider>
  );
}

export default memo(CatalogCsvUploadProvider);
